import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useAddOnMutation,
  useAppointment,
  useBarbers,
  useCancelAppointment,
  useCancelRecurrence,
  usePayAppointmentPix,
  useRecurrences,
  useServices,
} from '@/hooks/use-catalog';
import { appointmentDueCents, appointmentTotalCents, serviceLabel } from '@barber/shared';
import { PageHeader } from '@/components/layout/app-shell';
import { PixPayment } from '@/components/pix-payment';
import {
  ApiError,
  Badge,
  Button,
  Card,
  ErrorState,
  Feedback,
  LoadingState,
  describeRecurrence,
  formatDateTime,
  formatDuration,
  formatMoney,
  statusLabel,
  useNow,
  useTenant,
} from '@barber/ui';
import type { Appointment, PixCharge } from '@barber/ui';

/**
 * Um horário, com o que dá para fazer com ele.
 *
 * ---
 *
 * O PRAZO DE CANCELAMENTO É EXPLICADO ANTES, NÃO DEPOIS
 *
 * A barbearia define até quantas horas antes o cliente pode cancelar pelo app.
 * Passado o prazo, o servidor recusa — e receber essa recusa depois de tocar no
 * botão é a pior forma de descobrir a regra. Aqui o botão some e o texto diz o
 * porquê, com o telefone da barbearia à mão.
 */
export function AppointmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { tenant, locale } = useTenant();

  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [encerrandoFixo, setEncerrandoFixo] = useState(false);

  const consulta = useAppointment(id);
  const cancelar = useCancelAppointment();
  const recorrencias = useRecurrences();
  const encerrarFixo = useCancelRecurrence();
  const servicos = useServices();
  const profissionais = useBarbers();
  const encaixe = useAddOnMutation();

  // O prazo pode vencer com a tela aberta. Lido como estado externo, o botão
  // some sozinho na virada em vez de continuar oferecendo o que o servidor já
  // vai recusar.
  const agora = useNow();

  const appointment = consulta.data;
  const recemCriado = params.get('novo') === '1';

  if (consulta.isPending) return <LoadingState />;

  if (consulta.isError || !appointment) {
    return (
      <ErrorState
        title="Horário não encontrado"
        description="Ele pode ter sido cancelado ou pertencer a outra conta."
        onRetry={() => void consulta.refetch()}
      />
    );
  }

  const emAberto = appointment.status === 'PENDING' || appointment.status === 'CONFIRMED';
  const inicio = new Date(appointment.startsAt).getTime();
  const limite = inicio - tenant.booking.cancellationDeadlineHours * 3_600_000;
  const dentroDoPrazo = agora < limite;

  const precoFinal = appointmentTotalCents(appointment);

  const encaixesNoHorario = appointment.addOns ?? [];
  const fazPeloBarbeiro = new Set(
    profissionais.data?.find((b) => b.id === appointment.barber.id)?.serviceIds ?? [],
  );
  const encaixesPossiveis = (servicos.data ?? []).filter(
    (s) =>
      s.isAddOn &&
      fazPeloBarbeiro.has(s.id) &&
      !encaixesNoHorario.some((a) => a.serviceId === s.id),
  );
  // Mexer no encaixe segue o prazo do cancelamento: depois dele a barbearia já
  // se organizou para o atendimento como está.
  const podeMexerNoEncaixe = emAberto && dentroDoPrazo;

  async function alterarEncaixe(serviceId: string, acao: 'add' | 'remove'): Promise<void> {
    if (!id) return;

    setErro(null);

    try {
      await encaixe.mutateAsync({ appointmentId: id, serviceId, acao });
    } catch (error) {
      setErro(
        error instanceof ApiError
          ? error.message
          : 'Não conseguimos mudar o encaixe. Tente de novo.',
      );
    }
  }

  // A semana pode ser de um horário fixo já encerrado — aí não há o que encerrar.
  const fixo = (recorrencias.data ?? []).find(
    (r) => r.id === appointment.recurrenceId && r.status !== 'CANCELLED',
  );

  async function confirmarEncerramento(): Promise<void> {
    if (!fixo) return;

    setErro(null);

    try {
      await encerrarFixo.mutateAsync(fixo.id);
      navigate('/horarios', { replace: true });
    } catch (error) {
      setErro(
        error instanceof ApiError ? error.message : 'Não conseguimos encerrar. Tente novamente.',
      );
      setEncerrandoFixo(false);
    }
  }

  async function confirmarCancelamento(): Promise<void> {
    if (!id) return;

    setErro(null);

    try {
      await cancelar.mutateAsync({ id });
      navigate('/horarios', { replace: true });
    } catch (error) {
      setErro(
        error instanceof ApiError ? error.message : 'Não conseguimos cancelar. Tente novamente.',
      );
      setConfirmando(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Seu horário" />

      {recemCriado && <Feedback tone="success">Pronto! Seu horário está garantido.</Feedback>}

      <Card className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-bold text-content">
              {formatDateTime(appointment.startsAt, locale)}
            </p>
            <p className="mt-1 text-sm text-content-muted">
              {formatDuration(appointment.service.durationMinutes)}
            </p>
          </div>

          <Badge
            tone={
              appointment.status === 'CONFIRMED'
                ? 'success'
                : appointment.status === 'PENDING'
                  ? 'warning'
                  : 'neutral'
            }
          >
            {statusLabel(appointment.status)}
          </Badge>
        </div>

        <dl className="flex flex-col gap-2 border-t border-border pt-4 text-sm">
          <Linha
            rotulo="Serviço"
            valor={serviceLabel(
              appointment.service.name,
              encaixesNoHorario.map((a) => a.service),
            )}
          />
          <Linha rotulo="Profissional" valor={appointment.barber.name} />
          {fixo && <Linha rotulo="Horário fixo" valor={describeRecurrence(fixo)} />}
          <Linha
            rotulo="Valor"
            valor={precoFinal === 0 ? 'Incluso no plano' : formatMoney(precoFinal, locale)}
          />
          {(appointment.paidCents ?? 0) > 0 && (
            <Linha
              rotulo="Pagamento"
              valor={
                appointmentDueCents(appointment) === 0
                  ? 'Pago'
                  : `${formatMoney(appointment.paidCents ?? 0, locale)} já pago · falta ${formatMoney(
                      appointmentDueCents(appointment),
                      locale,
                    )}`
              }
            />
          )}
          {appointment.cancelReason && (
            <Linha rotulo="Motivo do cancelamento" valor={appointment.cancelReason} />
          )}
        </dl>
      </Card>

      {emAberto && (
        <PixDoHorario
          appointment={appointment}
          automatico={recemCriado && params.get('pix') === '1'}
          agora={agora}
          onPaid={() => void consulta.refetch()}
        />
      )}

      {(encaixesNoHorario.length > 0 || (podeMexerNoEncaixe && encaixesPossiveis.length > 0)) && (
        <Card className="flex flex-col gap-3 text-sm">
          <p className="font-semibold text-content">Encaixes</p>

          {encaixesNoHorario.map((a) => (
            <div key={a.serviceId} className="flex items-center justify-between gap-3">
              <span className="text-content">
                {a.service.name} · {formatMoney(a.priceCents, locale)}
              </span>
              {podeMexerNoEncaixe && (
                <Button
                  variant="ghost"
                  aria-label={`Tirar ${a.service.name}`}
                  loading={encaixe.isPending}
                  onClick={() => void alterarEncaixe(a.serviceId, 'remove')}
                >
                  Tirar
                </Button>
              )}
            </div>
          ))}

          {podeMexerNoEncaixe &&
            encaixesPossiveis.map((s) => (
              <Button
                key={s.id}
                variant="secondary"
                aria-label={`Acrescentar ${s.name}`}
                loading={encaixe.isPending}
                onClick={() => void alterarEncaixe(s.id, 'add')}
              >
                + {s.name} ({formatMoney(s.priceCents, locale)})
              </Button>
            ))}

          <p className="text-xs text-content-muted">
            O encaixe é feito entre um atendimento e outro: não aumenta o tempo do seu horário.
          </p>
        </Card>
      )}

      {erro && <Feedback tone="error">{erro}</Feedback>}

      {emAberto && (
        <div className="flex flex-col gap-3">
          {tenant.booking.allowCustomerReschedule && dentroDoPrazo && (
            <Link
              to={`/agendar?remarcar=${appointment.id}&servico=${appointment.service.id}`}
              className="tap-target inline-flex items-center justify-center rounded-xl border border-border bg-surface px-5 py-3 text-sm font-semibold text-content"
            >
              Remarcar
            </Link>
          )}

          {dentroDoPrazo ? (
            confirmando ? (
              <Card className="flex flex-col gap-3 border-sx-danger/40 bg-sx-danger/10">
                <p className="text-sm text-content">
                  Cancelar este horário? A vaga volta para a agenda na hora.
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    fullWidth
                    loading={cancelar.isPending}
                    onClick={() => void confirmarCancelamento()}
                  >
                    Sim, cancelar
                  </Button>

                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={() => setConfirmando(false)}
                    disabled={cancelar.isPending}
                  >
                    Manter
                  </Button>
                </div>
              </Card>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmando(true)}>
                {fixo ? 'Cancelar só esta semana' : 'Cancelar horário'}
              </Button>
            )
          ) : (
            <Card className="text-sm text-content-muted">
              O cancelamento pelo app é permitido até {tenant.booking.cancellationDeadlineHours}h
              antes.
              {tenant.contact.whatsapp && (
                <>
                  {' '}
                  <a
                    className="font-semibold text-brand underline"
                    href={`https://wa.me/${tenant.contact.whatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Fale com a barbearia
                  </a>
                  .
                </>
              )}
            </Card>
          )}
        </div>
      )}

      {fixo &&
        (encerrandoFixo ? (
          <Card className="flex flex-col gap-3 border-sx-danger/40 bg-sx-danger/10">
            <p className="text-sm text-content">
              Encerrar seu horário fixo de {describeRecurrence(fixo)}? As próximas semanas são
              liberadas. Se este horário já estiver dentro do prazo de cancelamento, ele fica
              mantido.
            </p>

            <div className="flex gap-2">
              <Button
                variant="danger"
                fullWidth
                loading={encerrarFixo.isPending}
                onClick={() => void confirmarEncerramento()}
              >
                Sim, encerrar
              </Button>

              <Button
                variant="secondary"
                fullWidth
                onClick={() => setEncerrandoFixo(false)}
                disabled={encerrarFixo.isPending}
              >
                Manter
              </Button>
            </div>
          </Card>
        ) : (
          <Button variant="ghost" onClick={() => setEncerrandoFixo(true)}>
            Encerrar horário fixo
          </Button>
        ))}

      <Link to="/horarios" className="py-2 text-center text-sm text-content-muted underline">
        Voltar para meus horários
      </Link>
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-content-muted">{rotulo}</dt>
      <dd className="text-right font-medium text-content">{valor}</dd>
    </div>
  );
}

/**
 * O PIX com desconto de um horário.
 *
 * Logo depois de marcar com "pagar agora", a cobrança é gerada sozinha. Numa
 * semana de horário fixo, o cliente pode pagar cada semana pelo detalhe dela,
 * até o horário. Fora disso não há oferta: o desconto é só na hora de marcar.
 */
function PixDoHorario({
  appointment,
  automatico,
  agora,
  onPaid,
}: {
  appointment: Appointment;
  automatico: boolean;
  agora: number;
  onPaid: () => void;
}) {
  const { tenant } = useTenant();
  const pagar = usePayAppointmentPix();
  const [cobranca, setCobranca] = useState<PixCharge | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const pedido = useRef(false);

  const pix = tenant.booking.pix;
  const falta = appointmentDueCents(appointment);

  function gerar(): void {
    setErro(null);
    pagar.mutate(appointment.id, {
      onSuccess: setCobranca,
      onError: (error) =>
        setErro(error instanceof ApiError ? error.message : 'Não conseguimos gerar o PIX agora.'),
    });
  }

  // Uma vez só, mesmo com a tela renderizando de novo.
  useEffect(() => {
    if (automatico && pix?.available && falta > 0 && !pedido.current) {
      pedido.current = true;
      gerar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automatico, pix?.available, falta]);

  if (cobranca) return <PixPayment charge={cobranca} onPaid={onPaid} />;
  if (erro) return <Feedback tone="error">{erro}</Feedback>;
  if (automatico && pagar.isPending) return <LoadingState />;

  const semanaDoFixo =
    Boolean(appointment.recurrenceId) && new Date(appointment.startsAt).getTime() > agora;
  if (!pix?.available || falta === 0 || !semanaDoFixo) return null;

  return (
    <Button variant="secondary" loading={pagar.isPending} onClick={gerar}>
      Pagar esta semana por PIX com {pix.discountPercent}% de desconto
    </Button>
  );
}
